import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNavBar from "../components/PublicNavBar";
import PublicFooter from "../components/PublicFooter";
import { Reveal, CountUp } from "../components/Animate";
import {
  BookOpen, Users, GraduationCap, ShieldCheck, BadgeCheck, Megaphone,
  Workflow, BarChart3, Award, TrendingUp, Sparkles, ArrowUpRight, ArrowRight,
  ChevronDown, FileText, Globe, Mail, X, AlertTriangle, FileSpreadsheet,
  Building2, Library, Upload, Brain,
} from "lucide-react";

const C = {
  green: "#006CFF", greenDeep: "#0F7C39", navy: "#0040A8", navyDeep: "#0A1F44",
  orange: "#FF8A00", purple: "#6C5CE7", red: "#DC2626",
  ink: "#0F172A", paper: "#F5F7FA", rule: "#E5E7EB",
};

function Pill({ children, color = C.green }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-widest uppercase px-3 py-1 rounded-full"
      style={{ background: `${color}15`, color }}>{children}</span>
  );
}

function FeatureCard({ icon: Icon, title, body, color, delay = 0 }) {
  return (
    <Reveal delay={delay} className="group rounded-2xl border bg-white p-6 float-hover hover:shadow-xl" style={{ borderColor: C.rule }}>
      <div className="w-12 h-12 rounded-xl grid place-items-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3" style={{ background: `${color}15`, color }}>
        <Icon size={24} />
      </div>
      <h3 className="font-display font-bold text-lg mt-4" style={{ color: C.ink }}>{title}</h3>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
    </Reveal>
  );
}

function FAQ({ q, a, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border bg-white" style={{ borderColor: C.rule }}>
      <button onClick={() => setOpen(v => !v)} className="w-full text-left p-5 flex items-center justify-between gap-4">
        <span className="font-semibold text-sm md:text-base" style={{ color: C.ink }}>{q}</span>
        <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} style={{ color: C.green }} />
      </button>
      <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden"><div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{a}</div></div>
      </div>
    </div>
  );
}

function StepNode({ n, icon: Icon, title, color }) {
  return (
    <div className="flex-1 min-w-[120px] flex flex-col items-center text-center">
      <div className="relative">
        <div className="w-16 h-16 rounded-2xl grid place-items-center text-white shadow-md transition-transform hover:scale-110"
          style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)` }}>
          <Icon size={26} />
        </div>
        <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border text-[10px] font-bold grid place-items-center"
          style={{ borderColor: C.rule, color: C.ink }}>{n}</span>
      </div>
      <div className="mt-3 font-bold text-sm" style={{ color: C.ink }}>{title}</div>
    </div>
  );
}
function Arrow() { return <div className="hidden md:flex items-center text-slate-300"><ArrowRight size={18} /></div>; }

export default function InstitutesPublic({ embedded = false }) {
  useEffect(() => { if (!embedded) window.scrollTo({ top: 0, behavior: "instant" }); }, [embedded]);

  return (
    <div className="min-h-screen bg-white text-ink">
      {!embedded && <PublicNavBar primaryCta={{ to: "/signup?role=institute", label: "Become a partner" }} />}

      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: C.paper }}>
        <div className="absolute inset-0 -z-10 opacity-60"
          style={{ background: `radial-gradient(60% 50% at 90% 0%, ${C.purple}18 0%, transparent 60%), radial-gradient(60% 50% at 0% 100%, ${C.green}12 0%, transparent 60%)` }} />
        <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Pill color={C.purple}>For Training Institutes</Pill>
            <h1 className="font-display font-black mt-5 leading-[1.05] text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase" style={{ color: C.navyDeep }}>
              Teach. <span style={{ color: C.green }}>Certify.</span> Place.<br />
              <span style={{ color: C.green }}>One platform.</span>
            </h1>
            <p className="text-slate-600 mt-5 text-lg max-w-xl leading-relaxed">
              Run your courses and assessments on SkillConnect, issue verified certificates, and place your trainees directly with our 200+ partner companies.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/signup?role=institute" data-testid="hero-cta-partner" className="px-6 py-3.5 rounded-lg font-semibold text-white inline-flex items-center gap-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                style={{ background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDeep} 100%)` }}>
                Become a partner <ArrowUpRight size={16} />
              </Link>
              <a href="#how" className="px-6 py-3.5 rounded-lg font-semibold inline-flex items-center gap-2 border-2 transition-all duration-300 hover:-translate-y-1"
                style={{ borderColor: C.navy, color: C.navy }}>
                See how it works <ArrowRight size={16} />
              </a>
            </div>
          </div>

          {/* Right preview card */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-white shadow-2xl border p-6 relative overflow-hidden" style={{ borderColor: C.rule }}>
              <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl opacity-30" style={{ background: C.purple }} />
              <div className="relative">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest" style={{ color: C.navy }}>
                  <BookOpen size={14} /> Institute Cockpit
                </div>
                <div className="grid grid-cols-3 gap-3 mt-4">
                  {[
                    { v: 248,  l: "Active learners",   c: C.navy },
                    { v: 86,   l: "Certified",         c: C.green },
                    { v: 41,   l: "Placed",            c: C.purple },
                  ].map((s, i) => (
                    <Reveal key={s.l} delay={i * 100} className="rounded-lg p-3 border" style={{ borderColor: C.rule }}>
                      <div className="font-display font-black text-xl" style={{ color: s.c }}>
                        <CountUp value={s.v} duration={1500} />
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{s.l}</div>
                    </Reveal>
                  ))}
                </div>
                <div className="mt-4 rounded-lg p-3 border" style={{ borderColor: C.rule }}>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Course Catalogue</div>
                  <div className="mt-2 space-y-2 text-sm">
                    {[
                      ["Embedded Systems Bootcamp", "12 wk", "92% pass"],
                      ["EV Powertrain Design",       "10 wk", "85% pass"],
                      ["VLSI Foundation",            "16 wk", "78% pass"],
                    ].map(([t, d, p]) => (
                      <div key={t} className="flex justify-between items-center" data-testid={`course-row-${t}`}>
                        <span className="font-semibold truncate" style={{ color: C.ink }}>{t}</span>
                        <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{d} · {p}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-3 rounded-lg p-3 border text-center" style={{ borderColor: C.rule, background: `${C.purple}08` }}>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Placement rate</div>
                  <div className="font-display font-black text-2xl mt-1" style={{ color: C.purple }}>
                    <CountUp value={47.7} decimals={1} suffix="%" duration={1700} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Promise bar */}
        <div className="border-t bg-white" style={{ borderColor: C.rule }}>
          <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              [BookOpen,    "Course Builder",      "Modules + assessments", C.purple],
              [Award,       "Verified Certificates", "Tamper-proof badges",   C.green],
              [Megaphone,   "Lead Generation",     "Discover new learners",   C.orange],
              [BarChart3,   "Placement Analytics", "Show real outcomes",      C.navy],
            ].map(([Icon, t, s, color]) => (
              <div key={t} className="group flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg grid place-items-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3" style={{ background: `${color}15`, color }}>
                  <Icon size={20} />
                </div>
                <div className="leading-tight"><div className="font-bold text-sm" style={{ color: C.ink }}>{t}</div><div className="text-xs text-slate-500">{s}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHALLENGES */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.red}>Today&apos;s institute problem</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              Quality teaching, <span style={{ color: C.red }}>invisible outcomes.</span>
            </h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-6 mt-12 items-start">
            <ul className="lg:col-span-2 space-y-3">
              {[
                "Trainee placement data lives in spreadsheets",
                "Certificates aren't verifiable by recruiters",
                "No direct channel to hiring companies",
                "Hard to attract new students without proof",
                "Manual content / assessment management",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3 p-4 rounded-xl bg-white border" style={{ borderColor: C.rule }}>
                  <span className="w-9 h-9 shrink-0 rounded-lg grid place-items-center" style={{ background: `${C.red}10`, color: C.red }}><X size={18} /></span>
                  <span className="text-sm font-medium pt-1.5" style={{ color: C.ink }}>{p}</span>
                </li>
              ))}
            </ul>
            <div className="rounded-2xl p-6 border-2 border-dashed text-center" style={{ borderColor: `${C.red}55`, background: `${C.red}05` }}>
              <AlertTriangle size={28} className="mx-auto" style={{ color: C.red }} />
              <div className="font-display font-black text-lg mt-3" style={{ color: C.red }}>WITHOUT SKILLCONNECT</div>
              <div className="mt-4 text-sm space-y-2 text-slate-700">
                <div>Untracked Outcomes</div>
                <div>Low Recruiter Trust</div>
                <div>Slow Student Growth</div>
                <div className="font-semibold" style={{ color: C.red }}>Limited Placements</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW WE HELP */}
      <section id="how" className="border-y" style={{ background: C.paper, borderColor: C.rule }}>
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.green}>What SkillConnect adds for institutes</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              Run your academy. We do the <span style={{ color: C.green }}>tech and the placements.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            <FeatureCard icon={BookOpen}     color={C.purple} delay={0}   title="Course & Assessment Builder"
              body="Build modules, lessons, rich content and proctored assessments — no engineering required." />
            <FeatureCard icon={BadgeCheck}   color={C.green}  delay={80}  title="Issue Verified Certificates"
              body="Every passing trainee earns a tamper-proof credential with a public verify URL." />
            <FeatureCard icon={Megaphone}    color={C.orange} delay={160} title="Inbound Student Leads"
              body="List your courses on SkillConnect's catalogue and capture leads from learners actively searching." />
            <FeatureCard icon={Workflow}     color={C.navy}   delay={240} title="Direct Placement Channel"
              body="Push trainees into our 200+ partner companies' shortlist with one click." />
            <FeatureCard icon={Library}      color={C.green}  delay={320} title="Co-Branded Content"
              body="Showcase your brand alongside the SkillConnect CoE network for cross-promotion." />
            <FeatureCard icon={BarChart3}    color={C.purple} delay={400} title="Outcomes Dashboard"
              body="Live metrics: pass rate, placement %, salary bands. Share with parents, sponsors, auditors." />
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.orange}>Trainee journey</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              Six stages. <span style={{ color: C.green }}>One pipeline.</span>
            </h2>
          </div>
          <div className="mt-12 flex flex-wrap items-start justify-center gap-y-8 gap-x-3">
            <StepNode n="01" icon={Users}        title="Enroll"             color={C.green} /><Arrow />
            <StepNode n="02" icon={FileText}     title="Learn modules"      color={C.green} /><Arrow />
            <StepNode n="03" icon={Brain}        title="Pass assessment"    color={C.navy} /><Arrow />
            <StepNode n="04" icon={Award}        title="Earn certificate"   color={C.purple} /><Arrow />
            <StepNode n="05" icon={Building2}    title="Match with company" color={C.orange} /><Arrow />
            <StepNode n="06" icon={GraduationCap} title="Get placed"        color={C.green} />
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="border-y" style={{ background: C.paper, borderColor: C.rule }}>
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.purple}>Outcomes you can publish</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              Numbers your sponsors will love.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Users,       v: 25,    suffix: "+",   l: "Partner Institutes",   c: C.purple },
              { icon: BadgeCheck,  v: 2500,  suffix: "+",   l: "Certificates Issued",  c: C.green  },
              { icon: TrendingUp,  v: 47.7,  suffix: "%", decimals: 1, l: "Avg Placement Rate", c: C.navy },
              { icon: Sparkles,    v: 78,    suffix: "%",   l: "Trainee NPS",          c: C.orange },
            ].map((s, i) => (
              <Reveal key={s.l} delay={i * 100} className="rounded-2xl border bg-white p-5 float-hover" style={{ borderColor: C.rule }}>
                <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: `${s.c}15`, color: s.c }}><s.icon size={22} /></div>
                <div className="mt-4 font-display font-black text-2xl md:text-3xl tracking-tight" style={{ color: s.c }}>
                  <CountUp value={s.v} suffix={s.suffix} decimals={s.decimals || 0} />
                </div>
                <div className="mt-1 text-sm font-medium text-slate-600">{s.l}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center">
            <Pill color={C.navy}>FAQs</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>For training institutes & academies.</h2>
          </div>
          <div className="mt-10 space-y-3">
            <FAQ defaultOpen q="What does it cost to join?"
              a="Onboarding is free. We share revenue from successful placements (transparent, success-fee based). For premium features (white-label cockpit, advanced analytics) we offer a low monthly subscription." />
            <FAQ q="Can we keep our brand on certificates?"
              a="Yes — every certificate is co-branded: your logo + SkillConnect's verification network. Recruiters trust the verification badge while your institute keeps its identity." />
            <FAQ q="Do we keep our trainee data?"
              a="100%. You own your learner roster, course content, and outcomes. SkillConnect is a delivery + placement layer — never a data lock-in." />
            <FAQ q="How does the placement connection work?"
              a="Once a trainee earns a verified certificate, they appear in our recruiter feed with your institute attribution. Recruiters can shortlist them directly, and you get credit on every hire." />
            <FAQ q="What roles / domains do you support?"
              a="Curriculum spans semiconductors, EVs, MedTech, AI hardware, smart manufacturing, agritech, electronics, cybersecurity, ADAS and IoT. Admin can add or modify pathways on-demand — no code changes required." />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="text-white relative overflow-hidden" style={{ background: C.navyDeep }}>
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full opacity-25 blur-3xl" style={{ background: C.green }} />
        <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: C.purple }} />
        <div className="relative max-w-5xl mx-auto px-6 py-20 text-center">
          <Pill color={C.green}>Partner with SkillConnect</Pill>
          <h2 className="font-display font-black text-3xl md:text-5xl mt-4 leading-tight">
            TEACH. <span style={{ color: C.green }}>CERTIFY.</span> PLACE.<br />
            ALL FROM ONE COCKPIT.
          </h2>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/signup?role=institute" className="px-7 py-4 rounded-lg font-bold text-white inline-flex items-center gap-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:scale-[1.02]" style={{ background: C.green }} data-testid="final-cta-partner">
              Become a partner <ArrowUpRight size={18} />
            </Link>
            <a href="mailto:info@skillconnect.com" className="px-7 py-4 rounded-lg font-bold inline-flex items-center gap-2 border-2 border-white/30 text-white transition-all duration-300 hover:bg-white/10 hover:-translate-y-1">
              Talk to partnerships
            </a>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-white/80">
            <div className="flex flex-wrap gap-6">
              <span className="inline-flex items-center gap-2"><Globe size={14} style={{ color: C.green }} /> skillconnect.com</span>
              <span className="inline-flex items-center gap-2"><Mail size={14} style={{ color: C.green }} /> info@skillconnect.com</span>
            </div>
            <div className="font-display font-extrabold text-xs tracking-widest uppercase" style={{ color: C.green }}>
              Quality Training · Verified Outcomes
            </div>
          </div>
        </div>
      </section>
      {!embedded && <PublicFooter />}
    </div>
  );
}
